import classes from "./LeaderboardContent.module.css";
import { useEffect, useState } from "react";
import Leaderboard from "./Leaderboard";
import ChallengeCountdown from "./ChallengeCountdown/ChallengeCountdown";
import ExpandIcon from "../../icon/ExpandIcon";
import DiscordAltIcon from "../../icon/DiscordAltIcon";
import OutlinedYouTubeIcon from "../../icon/OutlinedYouTubeIcon";
import OutlinedXIcon from "../../icon/OutlinedXIcon";
import OutlinedInstagramIcon from "../../icon/OutlinedInstagramIcon";
import OutlinedFacebookIcon from "../../icon/OutlinedFacebookIcon";
import HistoryOverlay from "./HistoryOverlay";
import { env } from "../../constants/env";
import { IoChevronDown, IoChevronUp } from "react-icons/io5";
import useWindowDimensions from "../../hooks/getWindowDimensions";
import { useUser } from "../../context/UserContext";
import { TbSquareRoundedMinus, TbSquareRoundedPlus } from "react-icons/tb";
import { parseMonthAndYear } from "../../utilities/functions";

const checklistItems = [
  {
    text: "✅ Join the challenge for free, no upfront payment required",
  },
  {
    text: "📈 Trade on a simulated account with 1:50 leverage in a 2-step challenge",
  },
  {
    text: "🏆 First 10% of traders to pass get funded",
  },
  {
    text: (
      <>
        💳 Pay the activation fee to claim your funded account.{" "}
        <a href="https://docs.propfunding.com/" target="_blank">
          Learn more
        </a>
      </>
    ),
  },
];

const baseData = [
  {
    rank: 1,
    user: "Alex Cortez",
    equity: "$145,320",
    stage: "Two",
    status: "Passed",
    drawdown: "-2",
  },
  {
    rank: 2,
    user: "Riley Chen",
    equity: "$145,320",
    stage: "Two",
    status: "In progress",
    drawdown: "-2",
  },
  {
    rank: 3,
    user: "Sam Adeyemi",
    equity: "$145,320",
    stage: "Two",
    status: "In progress",
    drawdown: -2,
  },
  {
    rank: 4,
    user: "Camrin Roczey",
    equity: "$134,500",
    stage: "one",
    status: "In progress",
    drawdown: -2,
  },
  {
    rank: 5,
    user: "Leo Tanaka",
    equity: "$130,380",
    stage: "one",
    status: "In progress",
    drawdown: -2,
  },
];

const fillerRowsBeforeRing = Array.from({ length: 1 }, (_, i) => ({
  rank: 6 + i,
  user: "Jane Doe",
  equity: "0.007%",
  stage: "one",
  status: "In progress",
  drawdown: -2,
  blurred: false,
}));

const outsideRingRow = {
  rank: 6,
  isDivider: true,
  dark: true,
  label: "💸 Outside the funding zone",
};

const fillerRowsAfterRing = Array.from({ length: 20 }, (_, i) => ({
  rank: 7 + i,
  user: "Jane Doe",
  equity: "0.007%",
  stage: "one",
  status: "In progress",
  drawdown: "-2",
  blurred: false,
  dark: true,
}));

const allFillerRows = [
  ...fillerRowsBeforeRing,
  outsideRingRow,
  ...fillerRowsAfterRing,
];

const faqItems = [
  {
    question: "What is Proparena leaderboard?",
    answer:
      "To qualify, you must finish in the top 25% of traders based on your account equity at the end the tournament.",
  },
  {
    question: "How often is the leaderboard updated?",
    answer:
      "The prize pool is distributed according to your final ranking percentage, with higher-ranked traders receiving a larger share.",
  },
  {
    question: "How do I qualify for a payout?",
    answer:
      "Yes, you can use our calculator tool to estimate potential winnings based on your expected placement.",
  },
  {
    question: "How is the prize pool split?",
    answer:
      "The prize pool is funded by tournament entry fees and platform contributions.",
  },
  {
    question: "What happens if i go into drawdown",
    answer:
      "Your current standing and potential prize share are visible in your tournament dashboard.",
  },
];

export const InfoCard = ({
  title,
  items,
  isOpen,
  setIsOpen,
  isDesktop,
  className,
  customCloseIcon,
}) => (
  <div className={`${classes.infoCard} ${className} mb-3`}>
    <div
      className={`${classes.header} d-flex justify-content-between align-items-center`}
      onClick={() => setIsOpen(!isOpen)}
    >
      <h6 className="mb-0">{title}</h6>
      <span className="d-md-none">
        {/* only show on mobile */}
        {isOpen ? (
          customCloseIcon ? (
            <TbSquareRoundedMinus size={20} />
          ) : (
            <IoChevronUp />
          )
        ) : customCloseIcon ? (
          <TbSquareRoundedPlus size={20} />
        ) : (
          <IoChevronDown />
        )}
      </span>
    </div>

    {/* Show always on desktop, toggle on mobile */}
    {(isOpen || isDesktop) && (
      <ul className={classes.list}>
        {items.map((item, index) => (
          <li key={index} className={classes.listItem}>
            {item.icon}
            <p>{item.text}</p>
          </li>
        ))}
      </ul>
    )}
  </div>
);

export const DiscordBanner = () => (
  <div className={classes.discordCard}>
    <div className="d-flex align-items-center justify-content-between">
      <DiscordAltIcon />
      <button
        onClick={() => window.open("https://discord.gg/8z37khrMA6", "_blank")}
      >
        Join now
      </button>
    </div>
    <h2>PropFunding community</h2>
    <p>Join the fastest growing prop trading community</p>
    <hr />
    <p className={classes.followText}>Follow us also at:</p>
    <div className="d-flex align-items-center gap-2">
      <OutlinedFacebookIcon
        onClick={() =>
          window.open("https://www.facebook.com/getpropfunding", "_blank")
        }
      />
      <OutlinedInstagramIcon
        onClick={() =>
          window.open("https://www.instagram.com/getpropfunding", "_blank")
        }
      />
      <OutlinedXIcon />
    </div>
  </div>
);

const LeaderboardContent = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const [selectedMonth, setSelectedMonth] = useState("September - 2026");
  const [selectedYear, setSelectedYear] = useState(2026);
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSpinning, setIsSpinning] = useState(false);
  const { user } = useUser();
  const [selected, setSelected] = useState("1-Step");

  const [isOpen, setIsOpen] = useState(false);

  const [fundedAccountsAvailable, setFundedAccountsAvailable] = useState(null); // NEW
  const [fundedAccountsTotal, setFundedAccountsTotal] = useState(6); // NEW

  const { width } = useWindowDimensions();

  const isDesktop = width >= 768;
  const isMobile = width < 768;

  useEffect(() => {
    // fetchLeaderboard();
    fetchFundedAccountsAvailable();
  }, [selectedMonth, selectedYear, selected]); // 👈 add selected

  const fetchFundedAccountsAvailable = async () => {
    try {
      const { month, year } = parseMonthAndYear(selectedMonth, selectedYear);

      const monthIndex = new Date(`${month} 1, ${year}`).getMonth() + 1;

      const res = await fetch(
        `${env.apiUrl}/api/funded-accounts/available?month=${monthIndex}&year=${year}&challengeType=${selected}`
      );

      if (!res.ok) throw new Error("Failed to fetch funded accounts available");

      const data = await res.json();
      const total = data.total || 6;

      setFundedAccountsAvailable(data.available || 0);
      setFundedAccountsTotal(total);

      // 👇 pass fresh total
      fetchLeaderboard(total);
    } catch (err) {
      console.error(err);
      setFundedAccountsAvailable(0);
    }
  };

  const fetchLeaderboard = async (totalOverride) => {
    try {
      setLoading(true);

      const { month, year } = parseMonthAndYear(selectedMonth, selectedYear);

      const res = await fetch(
        `${env.apiUrl}/api/leaderboard?month=${month}&year=${year}&challengeType=${selected}`
      );

      if (!res.ok) throw new Error("Failed to fetch leaderboard");

      const data = await res.json();
      const allRankings = data.rankings || [];

      const effectiveTotal = totalOverride ?? fundedAccountsTotal;

      const lastMainRank =
        selectedMonth === "September"
          ? 8
          : selectedMonth === "October"
          ? 7
          : selectedMonth === "November"
          ? 7
          : selectedMonth === "December" || selectedMonth === "January - 2026" || selectedMonth === "February - 2026" || selectedMonth === "March - 2026" || selectedMonth === "April - 2026"  || selectedMonth === "May - 2026"   || selectedMonth === "June - 2026"   || selectedMonth === "July - 2026"    || selectedMonth === "August - 2026"    || selectedMonth === "September - 2026"
          ? effectiveTotal
          : 6;

      const mainLeaderboard = allRankings.filter(
        (entry) => entry.rank <= lastMainRank
      );

      const afterRing = allRankings
        .filter((entry) => entry.rank > lastMainRank)
        .map((entry) => ({
          ...entry,
          type: "AfterRing",
          dark: true,
          blurred: false,
        }));

      const dividerRow = {
        rank: lastMainRank,
        isDivider: true,
        dark: true,
        label: "💸 Outside the funding zone",
      };

      setLeaderboardData([...mainLeaderboard, dividerRow, ...afterRing]);
    } catch (err) {
      console.error(err);
      setLeaderboardData([]);
    } finally {
      setLoading(false);
      setTimeout(() => setIsSpinning(false), 500); // stop spin after fetch
    }
  };

  return (
    <div className={classes.wrapper}>
      {showHistory && <HistoryOverlay onClose={() => setShowHistory(false)} />}
      {isDesktop && !user?.account?.synced && (
        <div>
          <ChallengeCountdown />
        </div>
      )}
      <div className="row">
        <div
          className={`${
            isExpanded ? "col-md-11" : "col-md-9"
          } mb-md-0 mb-md-5 mb-4`}
        >
          <Leaderboard
            title="September Challenge"
            search
            allFillerRows={[]}
            baseData={leaderboardData}
            selectedMonth={selectedMonth}
            selectedYear={selectedYear}
            setSelectedMonth={setSelectedMonth}
            setShowHistory={(value) => setShowHistory(value)}
            isSpinning={isSpinning}
            setIsSpinning={setIsSpinning}
            fetchLeaderboard={fetchLeaderboard}
            loading={loading}
            setSelected={setSelected}
            selected={selected}
          />
        </div>

        <div
          className={`${
            isExpanded ? "col-md-1" : "col-md-3"
          } position-relative col-12`}
        >
          <div
            className={`${classes.expandIconContainer} ${
              isExpanded ? classes.expandedContainer : ""
            }`}
          >
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className={classes.expandButton}
            >
              <ExpandIcon
                style={{
                  transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.3s ease",
                }}
              />
            </button>
          </div>
          {isMobile && !user?.account?.synced && (
            <div>
              <ChallengeCountdown />
            </div>
          )}
          {!isExpanded && (
            <>
              <div
                className={`${classes.availableCard} d-md-flex d-none align-items-center justify-content-center mb-3`}
              >
                <div className="d-flex flex-column align-items-center ">
                  <h3>{fundedAccountsAvailable ?? "-"}</h3>
                  <p>Funded Accounts Available</p>
                </div>
              </div>
              <InfoCard
                title="How it Works:"
                items={checklistItems}
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                isDesktop={isDesktop}
              />
              <DiscordBanner />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeaderboardContent;
